import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import type { MetricWithAttribution } from 'web-vitals/attribution';

import { appVersion, NAVIGATOR } from '@personal-area/common';
import { SESSION_STORAGE } from '@personal-area/storage';

import {
  ATTRIBUTION_FIELDS,
  SESSION_KEY,
} from '../constants/web-vitals.constants';
import {
  AttributionFields,
  WebVitalsConnection,
  WebVitalsEvent,
} from '../models/web-vitals-event.model';
import { WebVitalsSession } from '../models/web-vitals-session.model';
import { WEB_VITALS_CONFIG } from '../tokens/config.token';

/** `randomUUID` needs Safari 15.4; browserslist allows `ios_saf >= 15`. */
function generateId(): string {
  const cryptoApi = globalThis.crypto;

  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Per metric: TypeScript does not correlate `name` with `attribution`. */
function projectAttribution(
  metric: MetricWithAttribution,
): AttributionFields | undefined {
  switch (metric.name) {
    case 'CLS':
      return pickScalars(metric, ATTRIBUTION_FIELDS.CLS);
    case 'FCP':
      return pickScalars(metric, ATTRIBUTION_FIELDS.FCP);
    case 'INP':
      return pickScalars(metric, ATTRIBUTION_FIELDS.INP);
    case 'LCP':
      return pickScalars(metric, ATTRIBUTION_FIELDS.LCP);
    case 'TTFB':
      return pickScalars(metric, ATTRIBUTION_FIELDS.TTFB);
    default:
      // Skipped, not half-populated. `satisfies never` fails the build when
      // a library bump adds a metric — see docs/TELEMETRY.md.
      metric satisfies never;

      return undefined;
  }
}

/** `keyof` constrains names, not types: `lcpEntry` is a legal key too. */
function pickScalars<M extends MetricWithAttribution>(
  metric: M,
  fields: readonly (keyof M['attribution'] & string)[],
): AttributionFields {
  const projected: Record<string, string | number> = { name: metric.name };
  // Typed as required, but a bfcache restore can report before it is set.
  const attribution: Partial<M['attribution']> = metric.attribution ?? {};

  for (const field of fields) {
    const value = attribution[field];

    if (typeof value === 'string' || typeof value === 'number') {
      projected[field] = value;
    }
  }

  return projected as AttributionFields;
}

@Injectable({ providedIn: 'root' })
export class WebVitalsService {
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _navigator = inject(NAVIGATOR);
  private readonly _sessionStorage = inject(SESSION_STORAGE);
  private readonly _config = inject(WEB_VITALS_CONFIG);

  private _session: WebVitalsSession | null = null;

  /** Guard order is load-bearing. */
  public async register(): Promise<void> {
    // Not `typeof window`: `applyDomino()` shims it onto the Node global before
    // SSR. `PerformanceObserver` is not shimmed, so registering there throws.
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    // NaN and negatives fail closed. Skipping the `on*` calls is the only way a
    // disabled app costs nothing — each one builds an observer.
    if (!(this._config.sampleRate > 0)) {
      return;
    }

    // Doubles as the run-once guard against a route change re-registering.
    if (this._session) {
      return;
    }

    // Before the sampling verdict, so a session that lost the flip records that
    // and stays on the same side across a bfcache restore.
    const session = this._resolveSession();

    if (!session.sampled) {
      return;
    }

    const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import(
      'web-vitals/attribution'
    );
    const report = (metric: MetricWithAttribution): void =>
      this._report(metric);

    // No options, deliberately — see docs/TELEMETRY.md.
    onCLS(report);
    onFCP(report);
    onINP(report);
    onLCP(report);
    onTTFB(report);
  }

  private _resolveSession(): WebVitalsSession {
    let session: WebVitalsSession | null = null;
    const raw = this._sessionStorage.getItem(SESSION_KEY);

    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);

        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          'id' in parsed &&
          typeof parsed.id === 'string' &&
          'sampled' in parsed &&
          typeof parsed.sampled === 'boolean'
        ) {
          session = { id: parsed.id, sampled: parsed.sampled };
        }
      } catch {
        // Corrupt value under a same-origin key; mint a fresh session.
      }
    }

    if (!session) {
      session = {
        id: generateId(),
        sampled: Math.random() < this._config.sampleRate,
      };

      try {
        this._sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      } catch {
        // QuotaExceededError: in-memory for this page load only.
      }
    }

    this._session = session;

    return session;
  }

  private _report(metric: MetricWithAttribution): void {
    if (!this._session) {
      return;
    }

    const attribution = projectAttribution(metric);

    if (!attribution) {
      return;
    }

    const event: WebVitalsEvent = {
      value: metric.value,
      rating: metric.rating,
      id: metric.id,
      navigationType: metric.navigationType,
      navigationId: metric.navigationId,

      sessionId: this._session.id,
      project: this._config.project,
      appVersion: appVersion(),

      // Last, as in fbs-static: LCP contributes a top-level `url`.
      ...attribution,
    };

    // Optional in v6.
    if (metric.navigationURL) {
      event.navigationURL = metric.navigationURL;
    }

    const connection = this._readConnection();

    if (connection) {
      event.connection = connection;
    }

    this._send(event);
  }

  /** Omitted rather than sent empty. */
  private _readConnection(): WebVitalsConnection | undefined {
    const connection = (
      this._navigator as Navigator & { connection?: WebVitalsConnection }
    ).connection;

    if (!connection) {
      return undefined;
    }

    const result: WebVitalsConnection = {};

    if (typeof connection.effectiveType === 'string') {
      result.effectiveType = connection.effectiveType;
    }

    if (typeof connection.rtt === 'number') {
      result.rtt = connection.rtt;
    }

    if (typeof connection.downlink === 'number') {
      result.downlink = connection.downlink;
    }

    return Object.keys(result).length ? result : undefined;
  }

  /**
   * Never `HttpClient`: CLS and INP report on page-hide, and an in-flight
   * request is cancelled on unload. A string body stays CORS-safelisted.
   */
  private _send(event: WebVitalsEvent): void {
    try {
      this._navigator.sendBeacon?.(
        this._config.endpoint,
        JSON.stringify(event),
      );
    } catch {
      // A dropped metric must never surface to the user.
    }
  }
}
